import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Modal, Form, Table } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import Swal from 'sweetalert2';

export default function TreatmentIndex() {
    const { t } = useContext(TranslatorContext);

    const [treatments, setTreatments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Store Modal States
    const [storeModal, setStoreModal] = useState(false);
    const [storeForm, setStoreForm] = useState({ name: '', price: '', home_visit_price: '' });
    const [storeErrors, setStoreErrors] = useState({});
    const [storeLoading, setStoreLoading] = useState(false);

    // Update Modal States
    const [updateModal, setUpdateModal] = useState(false);
    const [updateForm, setUpdateForm] = useState({ id: null, name: '', price: '', home_visit_price: '' });
    const [updateErrors, setUpdateErrors] = useState({});
    const [updateLoading, setUpdateLoading] = useState(false);

    useEffect(() => {
        fetchTreatments();
    }, []);

    const fetchTreatments = () => {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        axios.get(`${process.env.REACT_APP_API_URL}/treatment`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => {
                setTreatments(res.data.treatments || []);
            })
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    };

    // Store Modal Functions
    const openStoreModal = () => {
        setStoreForm({ name: '', price: '', home_visit_price: '' });
        setStoreErrors({});
        setStoreModal(true);
    };

    const handleStoreChange = (e) => {
        const { name, value } = e.target;
        setStoreForm(prev => ({ ...prev, [name]: value }));
        if (storeErrors[name]) {
            setStoreErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleStoreSubmit = async (e) => {
        e.preventDefault();
        setStoreLoading(true);
        setStoreErrors({});

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/treatment`,
                {
                    name: storeForm.name,
                    price: storeForm.price ? parseInt(storeForm.price) : null,
                    home_visit_price: storeForm.home_visit_price ? parseInt(storeForm.home_visit_price) : null
                },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                }
            );

            if (response.data.treatment) {
                fetchTreatments();
                setStoreModal(false);
                setStoreForm({ name: '', price: '', home_visit_price: '' });

                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: t('Treatment created successfully'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setStoreErrors(error.response.data.errors || {});

                Swal.fire({
                    icon: 'error',
                    title: t('validation_error'),
                    text: t('Please check the form for errors'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                console.error('Error creating treatment:', error);

                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('Failed to create treatment'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } finally {
            setStoreLoading(false);
        }
    };

    // Update Modal Functions
    const openUpdateModal = (item) => {
        setUpdateForm({
            id: item.id,
            name: item.name,
            price: item.price || '',
            home_visit_price: item.home_visit_price || ''
        });
        setUpdateErrors({});
        setUpdateModal(true);
    };

    const handleUpdateChange = (e) => {
        const { name, value } = e.target;
        setUpdateForm(prev => ({ ...prev, [name]: value }));
        if (updateErrors[name]) {
            setUpdateErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        setUpdateLoading(true);
        setUpdateErrors({});

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/treatment-update/${updateForm.id}`,
                {
                    name: updateForm.name,
                    price: updateForm.price ? parseInt(updateForm.price) : null,
                    home_visit_price: updateForm.home_visit_price ? parseInt(updateForm.home_visit_price) : null
                },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                }
            );

            if (response.data.treatment) {
                fetchTreatments();
                setUpdateModal(false);

                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: t('Treatment updated successfully'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setUpdateErrors(error.response.data.errors || {});

                Swal.fire({
                    icon: 'error',
                    title: t('validation_error'),
                    text: t('Please check the form for errors'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else if (error.response?.status === 404) {
                setUpdateErrors({ general: [error.response.data.message] });

                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: error.response.data.message || t('Treatment not found'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                console.error('Error updating treatment:', error);

                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('Failed to update treatment'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } finally {
            setUpdateLoading(false);
        }
    };

    // Delete Function
    const handleDelete = async (id) => {
        setDeleteLoading(true);
        const token = localStorage.getItem("authToken");

        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/treatment-delete/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchTreatments();

            Swal.fire({
                icon: 'success',
                title: t('deleted'),
                text: t('Treatment deleted successfully'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
        } catch (error) {
            console.error('Error deleting treatment:', error);

            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: error.response?.data?.message || t('Failed to delete'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
        } finally {
            setDeleteLoading(false);
        }
    };

    // Delete confirmation with SweetAlert
    const confirmDelete = (id) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: t("Want to delete this treatment?"),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('delete'),
            cancelButtonText: t('cancel')
        }).then((result) => {
            if (result.isConfirmed) {
                handleDelete(id);
            }
        });
    };

    if (loading) return <PageLayout><p>{t("loading")}...</p></PageLayout>;

    return (
        <PageLayout>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 style={{ color: "#777" }}>Treatment Services</h4>
                <ButtonComponent
                    className="btn"
                    style={{ backgroundColor: "#3182ce", color: "#fff" }}
                    onClick={openStoreModal}
                >
                    + Add Treatment
                </ButtonComponent>
            </div>

            <div className="card-container">
                <div className="p-4">
                    {treatments.length === 0 ? (
                        <div className="text-center text-muted py-5">
                            <i className="material-icons" style={{ fontSize: '48px' }}>inbox</i>
                            <p className="mt-2">No treatment services found</p>
                            <ButtonComponent
                                className="btn btn-sm mt-2"
                                style={{ backgroundColor: "#3182ce", color: "#fff" }}
                                onClick={openStoreModal}
                            >
                                + Add First Treatment
                            </ButtonComponent>
                        </div>
                    ) : (
                        <Table responsive hover borderless>
                            <thead>
                                <tr className="table-header">
                                    <th>Service Name</th>
                                    <th>Clinic Price</th>
                                    <th>Home Visit Price</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {treatments.map((treatment) => (
                                    <tr key={treatment.id} className="table-row">
                                        <td className="align-middle">
                                            <i
                                                className="material-icons delete-icon"
                                                onClick={() => confirmDelete(treatment.id)}
                                                style={{ cursor: 'pointer', marginRight: '12px', fontSize: '18px', color: '#dc3545' }}
                                            >delete</i>
                                            {treatment.name}
                                        </td>
                                        <td className="align-middle">
                                            {treatment.price ? `${treatment.price}` : '-'}
                                        </td>
                                        <td className="align-middle">
                                            {treatment.home_visit_price ? `${treatment.home_visit_price}` : '-'}
                                        </td>
                                        <td className="text-end align-middle">
                                            <button
                                                className="action-btn"
                                                onClick={() => openUpdateModal(treatment)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    color: '#3182ce'
                                                }}
                                            >
                                                <i className="material-icons" style={{ fontSize: "16px" }}>edit_note</i>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </div>
            </div>

            {/* Store Modal - Add Treatment */}
            <Modal
                show={storeModal}
                onHide={() => setStoreModal(false)}
                centered
                dialogClassName="custom-modal-dialog"
                contentClassName="custom-modal-content"
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        Add Treatment Service
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleStoreSubmit}>
                    <Modal.Body className="custom-modal-body">
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                className="custom-input"
                                value={storeForm.name}
                                onChange={handleStoreChange}
                                isInvalid={!!storeErrors.name}
                                placeholder="Enter treatment name"
                                autoFocus
                            />
                            <Form.Control.Feedback type="invalid">
                                {storeErrors.name?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Clinic Price
                            </Form.Label>
                            <Form.Control
                                type="number"
                                name="price"
                                className="custom-input"
                                value={storeForm.price}
                                onChange={handleStoreChange}
                                isInvalid={!!storeErrors.price}
                                placeholder="Enter clinic price (optional)"
                            />
                            <Form.Control.Feedback type="invalid">
                                {storeErrors.price?.[0]}
                            </Form.Control.Feedback>
                            <small className="text-muted">Price for clinic visit</small>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Home Visit Price
                            </Form.Label>
                            <Form.Control
                                type="number"
                                name="home_visit_price"
                                className="custom-input"
                                value={storeForm.home_visit_price}
                                onChange={handleStoreChange}
                                isInvalid={!!storeErrors.home_visit_price}
                                placeholder="Enter home visit price (optional)"
                            />
                            <Form.Control.Feedback type="invalid">
                                {storeErrors.home_visit_price?.[0]}
                            </Form.Control.Feedback>
                            <small className="text-muted">Price for home visit service</small>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary px-4"
                            onClick={() => setStoreModal(false)}
                        >
                            Cancel
                        </ButtonComponent>
                        <ButtonComponent
                            type="submit"
                            className="btn px-4"
                            style={{ backgroundColor: "#3182ce", color: "#fff" }}
                            disabled={storeLoading || deleteLoading}
                        >
                            {storeLoading && <span className="spinner-border spinner-border-sm me-2"></span>}
                            Save
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Update Modal - Edit Treatment */}
            <Modal
                show={updateModal}
                onHide={() => setUpdateModal(false)}
                centered
                dialogClassName="custom-modal-dialog"
                contentClassName="custom-modal-content"
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        Edit Treatment Service
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleUpdateSubmit}>
                    <Modal.Body className="custom-modal-body">
                        {updateErrors.general && (
                            <div className="alert alert-danger mb-3">
                                {updateErrors.general}
                            </div>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                className="custom-input"
                                value={updateForm.name}
                                onChange={handleUpdateChange}
                                isInvalid={!!updateErrors.name}
                                placeholder="Enter treatment name"
                            />
                            <Form.Control.Feedback type="invalid">
                                {updateErrors.name?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Clinic Price
                            </Form.Label>
                            <Form.Control
                                type="number"
                                name="price"
                                className="custom-input"
                                value={updateForm.price}
                                onChange={handleUpdateChange}
                                isInvalid={!!updateErrors.price}
                                placeholder="Enter clinic price (optional)"
                            />
                            <Form.Control.Feedback type="invalid">
                                {updateErrors.price?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Home Visit Price
                            </Form.Label>
                            <Form.Control
                                type="number"
                                name="home_visit_price"
                                className="custom-input"
                                value={updateForm.home_visit_price}
                                onChange={handleUpdateChange}
                                isInvalid={!!updateErrors.home_visit_price}
                                placeholder="Enter home visit price (optional)"
                            />
                            <Form.Control.Feedback type="invalid">
                                {updateErrors.home_visit_price?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary px-4"
                            onClick={() => setUpdateModal(false)}
                        >
                            Cancel
                        </ButtonComponent>
                        <ButtonComponent
                            type="submit"
                            className="btn px-4"
                            style={{ backgroundColor: "#3182ce", color: "#fff" }}
                            disabled={updateLoading}
                        >
                            {updateLoading && <span className="spinner-border spinner-border-sm me-2"></span>}
                            Update
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>
        </PageLayout>
    );
}